/**
 * Lead Sources Page (Settings)
 * Build and maintain the tenant's hierarchical lead source catalog
 * (e.g. "Online > Google > Google Ads").
 *
 * Each source can set UTM defaults. A blank UTM field inherits from the
 * nearest ancestor that sets it; the backend resolves this live
 * (effectiveUtm), so the form shows the parent's effective value as the
 * placeholder. Deactivating a source also deactivates all of its descendants;
 * reactivating only affects that one source. Sources with children or any
 * customer/property/job reference cannot be deleted.
 *
 * Only ADMIN and OFFICE_MANAGER may change lead sources (backend
 * requireRole('ADMIN', 'OFFICE_MANAGER')); other roles get a read-only tree.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, ILeadSourcesApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { canManageLeadSources } from '../../utils/permissions';
import { descendantIds, treeOrder, UTM_FIELD_LABELS } from '../../utils/leadSource';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
} from '../../components/formStyles';
import { StarterMark, useStarterRecords } from '../../components/StarterBadge';
import { Toast } from '../../components/Toast';

interface FormState extends Record<types.UtmField, string> {
  name: string;
  description: string;
  /** '' = top level. */
  parentId: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  parentId: '',
  active: true,
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
};

/** 44px touch target for row actions on phones; unchanged from md up. */
const ROW_TAP = 'min-h-[44px] md:min-h-0';

type Editing = { mode: 'create' } | { mode: 'edit'; source: types.LeadSource } | null;

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

function usageSummary(source: types.LeadSource): string {
  const parts: string[] = [];
  if (source.customerCount > 0) parts.push(plural(source.customerCount, 'customer'));
  if (source.propertyCount > 0) parts.push(plural(source.propertyCount, 'property', 'properties'));
  if (source.jobCount > 0) parts.push(plural(source.jobCount, 'job'));
  if (parts.length === 0 && source.customerHistoryCount > 0) parts.push('customer history');
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/** Why a source cannot be deleted, or null when it can. */
function deleteBlockedReason(source: types.LeadSource): string | null {
  if (source.childCount > 0) {
    return `Has ${plural(source.childCount, 'child source')}. Delete or move them first.`;
  }
  if (source.inUse) {
    return (
      'In use by customers (including past lead source history), properties, or jobs. ' +
      'Deactivate it instead.'
    );
  }
  return null;
}

export function LeadSourcesPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<ILeadSourcesApi>(() => createAPIClient(), []);
  const canManage = canManageLeadSources(user);
  const starter = useStarterRecords('LEAD_SOURCE');

  // State
  const [sources, setSources] = useState<types.LeadSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refreshSources = useCallback(async () => {
    setSources(await apiClient.listLeadSources());
  }, [apiClient]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await refreshSources();
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load lead sources'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [refreshSources]);

  const ordered = useMemo(() => treeOrder(sources), [sources]);
  const byId = useMemo(() => new Map(sources.map((s) => [s.id, s])), [sources]);

  // Valid parents: anything except the source being edited and its descendants (would create a cycle).
  const parentOptions = useMemo(() => {
    if (editing?.mode !== 'edit') return ordered;
    const excluded = new Set([editing.source.id, ...descendantIds(editing.source.id, sources)]);
    return ordered.filter((s) => !excluded.has(s.id));
  }, [editing, ordered, sources]);

  const selectedParent = form.parentId ? byId.get(form.parentId) : undefined;

  // Descendants that saving an active -> inactive change would also deactivate.
  const cascadeCount = useMemo(() => {
    if (editing?.mode !== 'edit' || !editing.source.active || form.active) return 0;
    return descendantIds(editing.source.id, sources).filter((id) => byId.get(id)?.active).length;
  }, [editing, form.active, sources, byId]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openCreate = (parentId = '') => {
    setEditing({ mode: 'create' });
    setForm({ ...EMPTY_FORM, parentId });
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const openEdit = (source: types.LeadSource) => {
    setEditing({ mode: 'edit', source });
    setForm({
      name: source.name,
      description: source.description ?? '',
      parentId: source.parentId ?? '',
      active: source.active,
      utmSource: source.utmSource ?? '',
      utmMedium: source.utmMedium ?? '',
      utmCampaign: source.utmCampaign ?? '',
      utmTerm: source.utmTerm ?? '',
      utmContent: source.utmContent ?? '',
    });
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;

    const name = form.name.trim();
    if (!name) {
      setFormError('Name is required');
      return;
    }
    if (name.length > 100) {
      setFormError('Name must be 100 characters or less');
      return;
    }

    // Send null (not omitted) for blank optional fields so edits can clear them;
    // a null UTM field inherits from the parent.
    const request: types.CreateLeadSourceRequest = {
      name,
      description: form.description.trim() || null,
      parentId: form.parentId || null,
      active: form.active,
    };
    for (const field of types.UTM_FIELDS) {
      request[field] = form[field].trim() || null;
    }

    try {
      setIsSaving(true);
      setFormError(null);

      if (editing.mode === 'edit') {
        await apiClient.updateLeadSource(editing.source.id, request);
      } else {
        await apiClient.createLeadSource(request);
      }

      closeForm();
      await refreshSources();
    } catch (err) {
      setFormError(
        getErrorMessage(err, editing.mode === 'edit' ? 'Failed to update lead source' : 'Failed to create lead source')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const runRowAction = async (source: types.LeadSource, action: () => Promise<unknown>, fallback: string) => {
    try {
      setBusyId(source.id);
      setError(null);
      await action();
      setConfirmDeleteId(null);
      if (editing?.mode === 'edit' && editing.source.id === source.id) closeForm();
      await refreshSources();
    } catch (err) {
      setConfirmDeleteId(null);
      setError(getErrorMessage(err, fallback));
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = (source: types.LeadSource) =>
    source.active
      ? runRowAction(source, () => apiClient.deactivateLeadSource(source.id), 'Failed to deactivate lead source')
      : runRowAction(
          source,
          () => apiClient.updateLeadSource(source.id, { active: true }),
          'Failed to reactivate lead source'
        );

  const handleDelete = (source: types.LeadSource) =>
    runRowAction(source, () => apiClient.deleteLeadSource(source.id), 'Failed to delete lead source');

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <Link to="/settings" className="inline-block py-2 sm:py-0 text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Lead Sources</h1>
        </div>
        {canManage && !editing && (
          <button onClick={() => openCreate()} className={`${primaryButtonClass} w-full sm:w-auto`}>
            Add Lead Source
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Add / Edit Form */}
      {canManage && editing && (
        <section className="bg-white rounded-lg shadow mb-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4 break-words">
                {editing.mode === 'edit' ? `Edit ${editing.source.pathLabel}` : 'New Lead Source'}
              </h2>

              {formError && <Toast kind="error">{formError}</Toast>}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="leadSourceName" className={labelClass}>
                    Name<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="leadSourceName"
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    disabled={isSaving}
                    maxLength={100}
                    placeholder="Google Ads"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="leadSourceParent" className={labelClass}>
                    Parent
                  </label>
                  <select
                    id="leadSourceParent"
                    value={form.parentId}
                    onChange={(e) => setField('parentId', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    <option value="">None (top level)</option>
                    {parentOptions.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.pathLabel}
                        {!source.active ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                  {editing.mode === 'edit' && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      A source cannot be moved under itself or its own children.
                    </p>
                  )}
                </div>

                <div className="sm:col-span-4">
                  <label htmlFor="leadSourceDescription" className={labelClass}>
                    Description
                  </label>
                  <input
                    id="leadSourceDescription"
                    type="text"
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    disabled={isSaving}
                    placeholder="Optional"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-2 flex items-end sm:pb-2.5">
                  <label htmlFor="leadSourceActive" className="inline-flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-gray-700">
                    <input
                      id="leadSourceActive"
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setField('active', e.target.checked)}
                      disabled={isSaving}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-medium">Active</span>
                  </label>
                </div>

                {cascadeCount > 0 && (
                  <div className="sm:col-span-6 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm text-amber-800">
                      Saving will also deactivate {plural(cascadeCount, 'active child source')} under this one.
                      Reactivating later only reactivates this source.
                    </p>
                  </div>
                )}

                <fieldset className="sm:col-span-6 min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
                  <legend className="px-1 text-sm font-medium text-gray-700">UTM defaults</legend>
                  <p className="mb-3 text-xs text-gray-500 break-words">
                    Leave a field blank to inherit it from{' '}
                    {selectedParent ? <span className="font-medium">{selectedParent.pathLabel}</span> : 'the parent'}.
                    Placeholders show the inherited value.
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-5">
                    {types.UTM_FIELDS.map((field) => {
                      const inherited = selectedParent?.effectiveUtm[field] ?? null;
                      return (
                        <div key={field}>
                          <label htmlFor={`leadSource-${field}`} className={labelClass}>
                            {UTM_FIELD_LABELS[field]}
                          </label>
                          <input
                            id={`leadSource-${field}`}
                            type="text"
                            value={form[field]}
                            onChange={(e) => setField(field, e.target.value)}
                            disabled={isSaving}
                            maxLength={255}
                            placeholder={inherited ? `Inherited: ${inherited}` : 'Not set'}
                            className={inputClass}
                          />
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={isSaving} className={`${secondaryButtonClass} w-full sm:w-auto`}>
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={`${primaryButtonClass} w-full sm:w-auto`}>
                {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save Changes' : 'Create Lead Source'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-3 sm:p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Tree */}
        {!isLoading &&
          (ordered.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No lead sources yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UTM</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                    {canManage && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ordered.map((source) => {
                    const isConfirming = confirmDeleteId === source.id;
                    const isBusy = busyId === source.id;
                    const blockedReason = deleteBlockedReason(source);
                    const utmEntries = types.UTM_FIELDS.filter((f) => source.effectiveUtm[f]);
                    return (
                      <tr key={source.id} className={source.active ? '' : 'bg-gray-50'}>
                        <td data-label="Name" data-primary className="px-4 py-3">
                          <div className="flex items-start min-w-0" style={{ paddingLeft: `${source.depth * 1.5}rem` }}>
                            {source.depth > 0 && <span className="mr-2 text-gray-300" aria-hidden="true">└</span>}
                            <div className="min-w-0 break-words">
                              <div className={`font-semibold ${source.active ? 'text-gray-900' : 'text-gray-500'}`}>
                                {source.name}
                              </div>
                              <StarterMark records={starter} entityId={source.id} name={source.name} />
                              {source.description && (
                                <div className="text-sm text-gray-500">{source.description}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td data-label="UTM" className="px-4 py-3 text-xs text-gray-600">
                          {utmEntries.length === 0 ? (
                            '—'
                          ) : (
                            <ul className="space-y-0.5 min-w-0 break-words">
                              {utmEntries.map((field) => {
                                const own = source[field] !== null;
                                return (
                                  <li key={field} title={own ? 'Set on this source' : 'Inherited from a parent'}>
                                    <span className="font-mono">{UTM_FIELD_LABELS[field]}</span>=
                                    <span className={own ? 'text-gray-900' : 'italic text-gray-400'}>
                                      {source.effectiveUtm[field]}
                                    </span>
                                    {!own && <span className="ml-1 text-gray-400">(inherited)</span>}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </td>
                        <td data-label="Status" className="px-4 py-3 text-sm">
                          <span className={source.active ? 'text-green-700 font-medium' : 'text-gray-500'}>
                            {source.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td data-label="Usage" className="px-4 py-3 text-sm text-gray-600">{usageSummary(source)}</td>
                        {canManage && (
                          <td className="px-4 py-3 text-right md:whitespace-nowrap">
                            {isConfirming ? (
                              <span className="flex flex-wrap items-center gap-x-3 md:inline-flex md:flex-nowrap">
                                <span className="w-full text-sm text-gray-700 md:w-auto">Delete this lead source?</span>
                                <button
                                  onClick={() => handleDelete(source)}
                                  disabled={isBusy}
                                  className={`${rowDangerActionClass} ${ROW_TAP}`}
                                >
                                  {isBusy ? 'Deleting...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  disabled={isBusy}
                                  className={`${rowActionClass} ${ROW_TAP}`}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <span className="flex flex-wrap items-center gap-x-4 md:inline-flex md:flex-nowrap">
                                <button
                                  onClick={() => openCreate(source.id)}
                                  disabled={isSaving || busyId !== null}
                                  aria-label={`Add a source under ${source.pathLabel}`}
                                  className={`${rowActionClass} ${ROW_TAP}`}
                                >
                                  Add Child
                                </button>
                                <button
                                  onClick={() => openEdit(source)}
                                  disabled={isSaving || busyId !== null}
                                  className={`${rowActionClass} ${ROW_TAP}`}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleToggleActive(source)}
                                  disabled={busyId !== null}
                                  title={
                                    source.active
                                      ? 'Also deactivates every source under this one'
                                      : 'Reactivates only this source'
                                  }
                                  className={`${rowActionClass} ${ROW_TAP}`}
                                >
                                  {isBusy ? 'Saving...' : source.active ? 'Deactivate' : 'Activate'}
                                </button>
                                {/* Disabled buttons do not show tooltips reliably, so the wrapper carries the title. */}
                                <span title={blockedReason ?? undefined}>
                                  <button
                                    onClick={() => setConfirmDeleteId(source.id)}
                                    disabled={busyId !== null || blockedReason !== null}
                                    aria-label={
                                      blockedReason
                                        ? `Cannot delete ${source.pathLabel}: ${blockedReason}`
                                        : `Delete ${source.pathLabel}`
                                    }
                                    className={`${rowDangerActionClass} ${ROW_TAP}`}
                                  >
                                    Delete
                                  </button>
                                </span>
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>
    </main>
  );
}
